import type {QueryClient} from '@tanstack/react-query'

import type {MemexItemModel} from '../../../../models/memex-item-model'
import {isPageParamsDataGrouped, isQueryPageOfItems} from '../../queries/query-data-helpers'
import {activeMemexItemsQueryFilter} from '../../queries/query-filters'
import {
  buildMemexGroupedItemsQueryKey,
  createGroupedItemsId,
  type PageTypeForGroupedItems,
} from '../../queries/query-keys'
import {type GroupedPageParamsQueryData, type MemexPageQueryData, pageParamForInitialPage} from '../../queries/types'
import {
  incrementMemexItemsTotalCount,
  incrementMemexItemsTotalCountForGroup,
  mergeQueryDataForMemexItemsTotalCounts,
} from '../memex-totals'
import {getPageParamsQueryDataForVariables, setPageParamsQueryDataForVariables} from '../page-params'
import {getQueryDataForGroupedItemsPage} from './get-query-data'
import {getActiveMemexItemsQueryVariables} from './item-helpers'
import {
  findMemexItemByIdInQueryClient,
  findMemexItemGlobalIndexByIdInQueryClient,
  getMemexItemModelsFromQueryClient,
} from './read-items'
import {removeMemexItemsFromQueryClient} from './remove-items'

/**
 * Adds a memex item to the client-side cache, based on a group that it belongs in.
 * This method does not allow for a specific index to be used, instead it will add the item
 * to the end of a group (i.e. the last page of the group), unless more data exists for the group,
 * on the server. In that scenario, we won't perform an optimistic update at all, because we would
 * be adding the item to the wrong page.
 * @param queryClient
 * @param newMemexItemModel
 * @param pageType
 * @returns
 */
export function addMemexItemForGroupToQueryClient(
  queryClient: QueryClient,
  newMemexItemModel: MemexItemModel,
  pageType: PageTypeForGroupedItems,
): boolean | undefined {
  const variables = getActiveMemexItemsQueryVariables(queryClient)
  const pageParamsQueryData = getPageParamsQueryDataForVariables(queryClient, variables)
  if (!isPageParamsDataGrouped(pageParamsQueryData)) {
    // We should never hit this, because if we're passing a `PageTypeForGroupedItems` then we should have grouped page params
    return
  }

  const groupedItemsId = createGroupedItemsId(pageType)

  // If we're unable to find page params for this groupedItemsId, it is because there aren't any items
  // yet and the server returns `groupedItems` sparsely. We will end up creating
  // an entry in the page params for this groupedItemsId as part of this process.
  // By including just the pageParamForInitialPage, we'll form the query key properly.
  const pageParamsForGroupId = pageParamsQueryData.groupedItems[groupedItemsId] || [pageParamForInitialPage]

  const lastPageParam = pageParamsForGroupId[pageParamsForGroupId.length - 1]

  const queryDataForLastPage = getQueryDataForGroupedItemsPage(queryClient, variables, pageType, lastPageParam)

  // If an item exists in a group in a project, and a user adds that same
  // item to a new group via the omnibar, the server will move the item to the new group.
  if (findMemexItemByIdInQueryClient(queryClient, newMemexItemModel.id)) {
    // Optimistically remove the item from the view if it's already present.
    removeMemexItemsFromQueryClient(queryClient, [newMemexItemModel.id])
  }

  const newTotalCounts = {
    totalCount: incrementMemexItemsTotalCount(queryClient, variables, 1),
    groups: {
      [pageType.groupId]: incrementMemexItemsTotalCountForGroup(queryClient, variables, pageType.groupId, 1),
    },
  }
  if (pageType.secondaryGroupId) {
    newTotalCounts.groups[pageType.secondaryGroupId] = incrementMemexItemsTotalCountForGroup(
      queryClient,
      variables,
      pageType.secondaryGroupId,
      1,
    )
  }

  if (queryDataForLastPage?.pageInfo.hasNextPage) {
    // If the last page of data for the group indicates that we have more data on the server, then we'll do nothing
    // as the optimistic update that we would make would be incorrect.
    mergeQueryDataForMemexItemsTotalCounts(queryClient, variables, newTotalCounts)
    return true
  }

  // Go ahead and add the item to the end of our last page of data for the group.
  const queryKey = buildMemexGroupedItemsQueryKey(variables, pageType, lastPageParam)

  if (queryDataForLastPage) {
    const newItemModels = [...queryDataForLastPage.nodes, newMemexItemModel]
    const newQueryData = {
      ...queryDataForLastPage,
      nodes: newItemModels,
    }

    queryClient.setQueryData<MemexPageQueryData>(queryKey, newQueryData)
  } else {
    // Getting here means that we're adding to an empty cell, so we know that the
    // new query data's nodes are just the item being added, and we don't have to worry
    // about pageInfo.
    queryClient.setQueryData<MemexPageQueryData>(queryKey, {
      nodes: [newMemexItemModel],
      pageInfo: {hasNextPage: false, hasPreviousPage: false},
    })

    // We will also have to update the page params, so that the query gets tracked.
    const newPageParams: GroupedPageParamsQueryData = {
      ...pageParamsQueryData,
      groupedItems: {...pageParamsQueryData.groupedItems, [groupedItemsId]: [pageParamForInitialPage]},
    }
    setPageParamsQueryDataForVariables(queryClient, variables, newPageParams)
  }

  mergeQueryDataForMemexItemsTotalCounts(queryClient, variables, newTotalCounts)
}

/**
 * Adds a MemexItemModel to our client-side state. Does nothing if item with id already exists.
 * Will insert item at index provided, or push at the end of the array if none provided.
 * Triggers a re-render by using a new array object for the updated list of items.
 * @param queryClient
 * @param newMemexItemModel Item to add to the query client
 * @param index An optional index to insert the item at in the array. If omitted, will add to the end of the array.
 */
export function addMemexItemToQueryClient(queryClient: QueryClient, newMemexItemModel: MemexItemModel, index?: number) {
  const foundIndex = findMemexItemGlobalIndexByIdInQueryClient(queryClient, newMemexItemModel.id)

  let itemAdded = false
  let globalIndex = 0

  const totalItems = getMemexItemModelsFromQueryClient(queryClient).length

  queryClient.setQueriesData<MemexPageQueryData>(activeMemexItemsQueryFilter, queryData => {
    if (!isQueryPageOfItems(queryData)) {
      return queryData
    }

    if (foundIndex !== -1) {
      // Do nothing if this item is already in the query data
      //
      // NOTE: In other cases a new object is returned, so even though we aren't changing
      // anything in the data here, return a new object to match the previous behavior.
      return {...queryData}
    }

    if (itemAdded) {
      return queryData
    }

    if (index != null && index >= globalIndex && index < globalIndex + queryData.nodes.length) {
      const newPageNodes = [...queryData.nodes]
      const indexOnPage = index - globalIndex
      newPageNodes.splice(indexOnPage, 0, newMemexItemModel)
      itemAdded = true
      return {
        ...queryData,
        nodes: newPageNodes,
      }
    } else if (index == null && globalIndex + queryData.nodes.length === totalItems) {
      itemAdded = true
      if (queryData.pageInfo.hasNextPage) {
        // We don't want to optimistically update the addition of the item
        // if we are not on the last page of all of the data - this will be incorrect.
        return queryData
      }
      // If no index was provided, we want to insert the item at the end of the last page
      const newPageNodes = [...queryData.nodes]
      newPageNodes.push(newMemexItemModel)
      return {
        ...queryData,
        nodes: newPageNodes,
      }
    } else {
      globalIndex += queryData.nodes.length
      return queryData
    }
  })

  if (itemAdded) {
    const variables = getActiveMemexItemsQueryVariables(queryClient)
    const totalCount = incrementMemexItemsTotalCount(queryClient, variables, 1)
    mergeQueryDataForMemexItemsTotalCounts(queryClient, variables, {totalCount})
  }
}
